import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { HeroSolidBGView, type HeroSolidBGContentProps } from './HeroSolidBG';

export function FloorPlansHero(props: HeroSolidBGContentProps) {
  const bgDefinition = contentSchema.slotGroups!['floorplanshero.bg_image'];
  const ctaDefinition = contentSchema.slotGroups!['floorplanshero.cta_link'];
  return (
    <>
      <HeroSolidBGView {...props} prefix="floorplanshero" bgDefinition={bgDefinition} />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent path="floorplanshero.title" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableToken path="floorplanshero.subtitle" type="richtext" label="Subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="floorplanshero.blurb" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="floorplanshero.bg_image" definition={bgDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="floorplanshero.cta_link" definition={ctaDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansHero',
  displayName: 'Floor Plans Hero',
  tokens: {
    'floorplanshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Floor Plans</p>',
    },
    'floorplanshero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Apartment Floor Plans in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'floorplanshero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultField: 'property_floorplans_blurb',
    },
  },
  slotGroups: {
    'floorplanshero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Apartment interior',
        },
      },
      generator: {
        prompt:
          'Pick the hero background photo for the Floor Plans page: an attractive apartment interior at this property, such as a kitchen, living space, or bedroom. Write alt text describing what the photo shows.',
        context:
          'Full-bleed background hero at the top of the Floor Plans page, behind a semi-opaque primary-color overlay with centered white headline text. Prefer staged or professional photos; skip blurry, dark, cluttered, or unappealing shots. Priority: relevance to apartment interiors first, image quality second, uniqueness third. Each inner page (Floor Plans, Amenities, Gallery, Virtual Tour, Neighborhood, Contact Us) has its own hero; the Virtual Tour hero also uses an interior, so pick a different room or photo from it. Do not reuse an image already used as another page hero or the homepage hero unless no other suitable image exists.',
      },
    },
    'floorplanshero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(FloorPlansHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansHero;
